// services/auth/oauthService.js - Cleaned version without API key storage
import { COGNITO_CONFIG } from '../../config/AuthApiEndpoints';
import { authService } from './AuthService';
import { asyncStorage } from '../storage/asyncStorageService';

class OAuthService {
  constructor() {
    this.pendingResult = null;
  }

  /**
   * Initiate Microsoft login
   */
  async initiateMicrosoftLogin(email = null) {
    try {
      // Generate CSRF token
      const csrfToken = Math.random().toString(36).substring(2, 15);

      // Determine client ID to use
      let clientInfo = {
        clientId: COGNITO_CONFIG.DEFAULT_CLIENT_ID,
        redirectUri: COGNITO_CONFIG.DEFAULT_REDIRECT_URI
      };

      // If email is provided, try to get domain-specific client
      if (email && email.includes('@')) {
        const domain = email.substring(email.indexOf('@') + 1).toLowerCase();
        clientInfo = await authService.fetchClientInfoForDomain(domain);
        sessionStorage.setItem('ms_auth_email', email);
      }

      // Encode clientId into state so it survives the redirect regardless of sessionStorage
      const state = `${csrfToken}|${clientInfo.clientId}`;
      sessionStorage.setItem('microsoft_auth_state', state);

      // Store client info for callback (fallback)
      sessionStorage.setItem('ms_auth_client_id', clientInfo.clientId);
      sessionStorage.setItem('ms_auth_redirect_uri', clientInfo.redirectUri);

      // Check if forcing fresh login
      const hadPreviousFailure = sessionStorage.getItem('ms_auth_failed') === 'true';

      // Build authorization URL
      const cognitoURL = new URL(`https://${COGNITO_CONFIG.COGNITO_DOMAIN}/oauth2/authorize`);
      cognitoURL.searchParams.append('client_id', clientInfo.clientId);
      cognitoURL.searchParams.append('response_type', 'code');
      cognitoURL.searchParams.append('scope', 'email openid profile');
      cognitoURL.searchParams.append('redirect_uri', clientInfo.redirectUri);
      cognitoURL.searchParams.append('identity_provider', COGNITO_CONFIG.IDENTITY_PROVIDERS.MICROSOFT);
      cognitoURL.searchParams.append('state', state);
      
      // Force fresh login if previous failure
      if (hadPreviousFailure) {
        cognitoURL.searchParams.append('prompt', 'login');
        cognitoURL.searchParams.append('max_age', '0');
        // Clear failure flag
        sessionStorage.removeItem('ms_auth_failed');
        sessionStorage.removeItem('ms_error_message');
      } else {
        cognitoURL.searchParams.append('prompt', 'select_account');
      }
      
      // Set pending auth flag
      asyncStorage.set('pendingAuthRequest', 'true');
      
      // Redirect to Microsoft login
      window.location.href = cognitoURL.toString();
    } catch (error) {
      this.fallbackMicrosoftLogin();
    }
  }

  /**
   * Initiate Google login
   */
  async initiateGoogleLogin(email = null) {
    try {
      // Generate state for CSRF protection
      const state = Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem('google_auth_state', state);
      
      // Store email if provided
      if (email) {
        sessionStorage.setItem('google_auth_email', email);
      }
      
      // Check if forcing fresh login
      const hadPreviousFailure = sessionStorage.getItem('google_auth_failed') === 'true';
      
      // Build authorization URL (using custom domain for Google)
      const authURL = new URL(`https://${COGNITO_CONFIG.CUSTOM_DOMAIN}/oauth2/authorize`);
      authURL.searchParams.append('client_id', COGNITO_CONFIG.DEFAULT_CLIENT_ID);
      authURL.searchParams.append('response_type', 'code');
      authURL.searchParams.append('scope', 'email openid profile');
      authURL.searchParams.append('redirect_uri', COGNITO_CONFIG.DEFAULT_REDIRECT_URI);
      authURL.searchParams.append('identity_provider', COGNITO_CONFIG.IDENTITY_PROVIDERS.GOOGLE);
      authURL.searchParams.append('state', state);
      
      // Force fresh login if previous failure
      if (hadPreviousFailure) {
        authURL.searchParams.append('prompt', 'login');
        authURL.searchParams.append('max_age', '0');
        // Clear failure flag
        sessionStorage.removeItem('google_auth_failed');
        sessionStorage.removeItem('google_error_message');
      } else {
        authURL.searchParams.append('prompt', 'select_account');
      }
      
      // Set pending auth flag
      asyncStorage.set('pendingAuthRequest', 'true');
      
      // Redirect to Google login
      window.location.href = authURL.toString();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fallback Microsoft login with default client
   */
  fallbackMicrosoftLogin() {
    // Generate state with default clientId encoded
    const csrfToken = Math.random().toString(36).substring(2, 15);
    const state = `${csrfToken}|${COGNITO_CONFIG.DEFAULT_CLIENT_ID}`;
    sessionStorage.setItem('microsoft_auth_state', state);
    
    // Store default client info
    sessionStorage.setItem('ms_auth_client_id', COGNITO_CONFIG.DEFAULT_CLIENT_ID);
    sessionStorage.setItem('ms_auth_redirect_uri', COGNITO_CONFIG.DEFAULT_REDIRECT_URI);
    
    // Build auth URL
    const cognitoURL = new URL(`https://${COGNITO_CONFIG.COGNITO_DOMAIN}/oauth2/authorize`);
    cognitoURL.searchParams.append('client_id', COGNITO_CONFIG.DEFAULT_CLIENT_ID);
    cognitoURL.searchParams.append('response_type', 'code');
    cognitoURL.searchParams.append('scope', 'email openid profile');
    cognitoURL.searchParams.append('redirect_uri', COGNITO_CONFIG.DEFAULT_REDIRECT_URI);
    cognitoURL.searchParams.append('identity_provider', COGNITO_CONFIG.IDENTITY_PROVIDERS.MICROSOFT);
    cognitoURL.searchParams.append('state', state);
    
    // Set pending auth flag
    asyncStorage.set('pendingAuthRequest', 'true');
    
    // Redirect
    window.location.href = cognitoURL.toString();
  }

  /**
   * Process auth code from OAuth callback
   */
  async processAuthCode(code, provider = 'unknown', options = {}) {
    try {
      const { state: byocState } = options;

      // Get stored email for domain resolution
      const emailHint = sessionStorage.getItem(`${provider}_auth_email`) ||
                       sessionStorage.getItem('ms_auth_email') ||
                       sessionStorage.getItem('google_auth_email');

      // Extract clientId from state param (survives redirects, incognito, tab restore)
      // State format: "<csrfToken>|<clientId>" — falls back to sessionStorage then empty
      const stateParam = new URLSearchParams(window.location.search).get('state');
      const clientId = stateParam?.split('|')[1] ||
                       sessionStorage.getItem('ms_auth_client_id') ||
                       '';

      // Process the auth code (API key handled by global interceptor)
      const response = await authService.handleCognitoCallback(code, emailHint, clientId, byocState);
      
      // Clear pending auth request flag
      asyncStorage.remove('pendingAuthRequest');

      // Clean up stored auth data. On success, also clear the stored email hint —
      // leaving it behind causes the NEXT user's OAuth login (same browser/tab) to
      // send a stale emailHint to /cognito/callback, which the backend rejects as
      // a mismatch. Only the failure path preserves the email (for signup pre-fill).
      this.cleanupStoredAuthData(provider, response.success);

      return response;
    } catch (error) {

      // Clear pending auth request flag even on error
      asyncStorage.remove('pendingAuthRequest');

      // Clean up stored auth data but keep email for potential signup
      this.cleanupStoredAuthData(provider, false);
      
      return {
        success: false,
        error: this.getErrorMessage(error, provider)
      };
    }
  }

  /**
   * Clean up stored auth data
   */
  cleanupStoredAuthData(provider, removeEmail = true) {
    // Clean up provider-specific data
    if (provider === 'microsoft' || provider === 'ms') {
      sessionStorage.removeItem('microsoft_auth_state');
      sessionStorage.removeItem('ms_auth_client_id');
      sessionStorage.removeItem('ms_auth_redirect_uri');
      if (removeEmail) {
        sessionStorage.removeItem('ms_auth_email');
      }
    } else if (provider === 'google') {
      sessionStorage.removeItem('google_auth_state');
      if (removeEmail) {
        sessionStorage.removeItem('google_auth_email');
      }
    } else if (provider === 'cognito') {
      sessionStorage.removeItem('cognito_auth_email');
    }
  }

  /**
   * Get error message for OAuth failures
   */
  getErrorMessage(error, provider) {
    if (error.response?.status === 403) {
      return `${provider} account not registered. Please sign up first.`;
    } else if (error.response?.status === 500) {
      return 'User not registered/found';
    } else if (error.message?.includes('User not found')) {
      return 'User not registered/found';
    } else if (error.message?.includes('email domain')) {
      return 'Your email domain is not authorized. Please contact your administrator.';
    }
    
    return `${provider} authentication failed. Please try again.`;
  }

  /**
   * Check if there's a pending auth request
   */
  hasPendingRequest() {
    return asyncStorage.get('pendingAuthRequest') === 'true';
  }
}

export const oauthService = new OAuthService();